import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { HeightProps, MinWidthProps, SizeProps } from 'styled-system';
import { backgroundColor, height, minWidth, size, space, width } from 'styled-system';

import type { BoxSizingProps } from '../../theme/system-utilities';
import { boxSizing, fill } from '../../theme/system-utilities';
import type { FlexProps } from '../Flex';
import { Flex } from '../Flex';
import Icon from '../Icon';

const BG_SIZES = ['56px', '60px', '72px', '80px'];
const ICON_SIZES = [38, 40, 42];

interface DefaultImageProps
  extends FlexProps, MinWidthProps, HeightProps, BoxSizingProps, SizeProps {
  title: string;
  href?: string;
  onClick?: () => void;
}

const UnstyledDefaultImageBackground = styled(Flex)<DefaultImageProps>`
  ${backgroundColor};
  ${minWidth};
  ${height};
  ${size};
  ${boxSizing};
`;

const DefaultImageBackground = (props: ComponentProps<typeof UnstyledDefaultImageBackground>) => (
  <UnstyledDefaultImageBackground
    bg="sys.neutral.background.weaker"
    minWidth={BG_SIZES}
    height={BG_SIZES}
    boxSizing="border-box"
    pt={['sp_4', null, 'sp_8']}
    justifyContent="center"
    alignItems="center"
    {...props}
  />
);

const UnstyledDefaultImage = styled(Icon)<any>`
  ${height};
  ${width};
  ${space};
  ${fill};
`;

const DefaultImage = (props: ComponentProps<typeof UnstyledDefaultImage>) => (
  <UnstyledDefaultImage
    name="defaultProductImage"
    height="auto"
    width={ICON_SIZES}
    role="img"
    fill="sys.neutral.icon.inactive"
    {...props}
  />
);

const DefaultProductImage = ({ title, href, ...props }: DefaultImageProps) => (
  <DefaultImageBackground
    data-testid="default-product-image"
    title={title}
    aria-label={title}
    {...(href ? { as: 'a', href } : {})}
    {...props}
  >
    <DefaultImage ariaLabel={title} />
  </DefaultImageBackground>
);

export default DefaultProductImage;
